'use client'

import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormTextarea,
  LargeFormInput,
} from './ui/form'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import { zodResolver } from '@hookform/resolvers/zod'
import dynamic from 'next/dynamic'
import { usePostHog } from 'posthog-js/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { cn } from 'src/utils'
import { bookDemo } from '~/app/actions'
import { DemoFormSchema, demoFormSchema } from '~/utils/forms'

const FormSuccess = dynamic(() => import('./form-success'))

const items = [
  {
    id: 'agent-scripting',
    label: 'Agent Scripting',
  },
  {
    id: 'conversation-analysis',
    label: 'Conversation Analysis',
  },
  {
    id: 'quality-management',
    label: 'Quality Management',
  },
] as const

const radioOpts = [
  {
    id: '1-50',
    label: '1-50',
  },
  {
    id: '51-250',
    label: '51-250',
  },
  {
    id: '251-500',
    label: '251-500',
  },
  {
    id: '501+',
    label: '501+',
  },
  {
    id: 'N/A',
    label: 'N/A',
  },
] as const

export function DemoForm() {
  const [step, setStep] = useState(1)
  const [success, setSuccess] = useState(false)
  const event = usePostHog()

  const form = useForm<DemoFormSchema>({
    resolver: zodResolver(demoFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      agents: '',
      industry: '',
      modules: [],
      message: '',
    },
  })

  async function onSubmit(values: DemoFormSchema) {
    try {
      if (step === 1) {
        event.capture('submitted "Book a Demo" form', { Product: values.modules })
        await bookDemo(values)
        setStep(2)
      } else {
        event.capture('completed "Book a Demo" form', {
          Product: values.modules,
          Industry: values.industry,
          Agents: values.agents,
        })
        await bookDemo(values)
        setSuccess(true)
        form.reset()
      }
    } catch (error) {
      event.capture('error submitting demo form', values)
    }
  }

  const closeSuccess = () => {
    window.location.href = '/'
  }

  function renderStep() {
    switch (step) {
      case 1:
        return (
          <section className="mb-8 space-y-6">
            <h1 className="text-5xl text-center font-800 tracking-wide mb-6">
              Let&apos;s explore how Happitu can work for you
            </h1>

            <FormLabel className="text-base">
              Who do we have the pleasure of speaking with?
            </FormLabel>
            <div className="flex justify-between space-x-4">
              <LargeFormInput
                autoFocus
                name="firstName"
                label="First Name"
                control={form.control}
              />
              <LargeFormInput name="lastName" label="Last Name" control={form.control} />
            </div>
            <LargeFormInput
              name="email"
              label="Work Email"
              type="email"
              control={form.control}
            />
            <LargeFormInput name="company" label="Company" control={form.control} />
            <FormField
              control={form.control}
              name="modules"
              render={() => (
                <FormItem>
                  <div className="mb-4 mt-10">
                    <FormLabel className="text-base">
                      Which CX modules are a priority for you?
                    </FormLabel>
                    <FormDescription className="text-xs text-secondary-mdgrey">
                      (Select all that apply)
                    </FormDescription>
                  </div>
                  {items.map((item) => (
                    <FormField
                      key={item.id}
                      control={form.control}
                      name="modules"
                      render={({ field }) => {
                        return (
                          <label key={item.id} className="cursor-pointer">
                            <FormItem
                              className={cn(
                                'flex flex-row w-auto items-center space-x-3 px-3 py-2 mb-1 bg-white rounded-md border-2 border-transparent cursor-pointer',
                                {
                                  'border-secondary-black': field.value?.includes(
                                    item.id,
                                  ),
                                },
                              )}
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(item.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, item.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== item.id,
                                          ),
                                        )
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer text-lg">
                                {item.label}
                              </FormLabel>
                            </FormItem>
                          </label>
                        )
                      }}
                    />
                  ))}
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="h-10" />
            <Button
              loading={form.formState.isSubmitting}
              className="w-full"
              type="submit"
              variant="primary"
              arrow
              rounded
            >
              Schedule your Demo
            </Button>
          </section>
        )
      case 2:
        return (
          <section className="mb-8 space-y-6">
            <hgroup>
              <h1 className="text-4xl text-center font-extrabold tracking-wide mb-6">
                You&apos;re all set!
              </h1>
              <p>A member of the Happitu team with reach out shortly to set up a time.</p>
              <p>
                In the meantime, tell us a little about your operation to help us prepare
                for your demo.
              </p>
            </hgroup>
            <FormField
              control={form.control}
              name="agents"
              render={({ field }) => {
                return (
                  <FormItem className="space-y-3">
                    <FormLabel>How many agents do you have?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="md:flex space-x-0"
                      >
                        {radioOpts.map((item) => (
                          <FormField
                            key={item.id}
                            control={form.control}
                            name="agents"
                            render={({ field }) => {
                              return (
                                <label key={item.id} className="w-full cursor-pointer">
                                  <FormItem
                                    className={cn(
                                      'flex items-center space-x-3 space-y-0 p-2 bg-white rounded-lg border-2 border-transparent cursor-pointer',
                                      {
                                        'border-secondary-black': field.value === item.id,
                                      },
                                    )}
                                  >
                                    <FormControl>
                                      <RadioGroupItem value={item.id} />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer">
                                      {item.label}
                                    </FormLabel>
                                  </FormItem>
                                </label>
                              )
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What industry are you in?</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select an industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Financial Services">
                        Financial Services
                      </SelectItem>
                      <SelectItem value="Retail/E-Commerce">Retail/E-Commerce</SelectItem>
                      <SelectItem value="Collections">Collections</SelectItem>
                      <SelectItem value="Insurance">Insurance</SelectItem>
                      <SelectItem value="Medical">Medical</SelectItem>
                      <SelectItem value="BPO">BPO</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid w-full gap-1.5">
              <FormTextarea
                control={form.control}
                placeholder="Type your message here."
                label="Any other details we should know about prior to your demo?"
                name="message"
              />
            </div>
            <div className="h-5" />
            <Button
              type="submit"
              variant="primary"
              rounded
              className="w-full"
              loading={form.formState.isSubmitting}
            >
              Complete Profile
            </Button>
          </section>
        )
    }
  }

  return (
    <div>
      <FormSuccess open={success} onOpenChange={closeSuccess} />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {renderStep()}

          <div className="h-[200px]" />
          <ol className="hidden md:grid grid-cols-4 fixed bg-background bottom-0 left-0 right-0 list-decimal list-inside text-sm">
            <li className="p-4 border-t-4 border-t-primary-80">
              <div className="flex gap-10">
                <div className="flex-grow">
                  <h3 className="font-600">A little bit about you</h3>
                  <p className="text-xs">Help us get to know you and your business.</p>
                </div>
                {<Button arrow variant="primary" size="sm"></Button>}
              </div>
            </li>
            <li
              className={cn('p-4 border-l border-t-4 border-t-secondary-tan', {
                'animate-[border-pulse_4s_infinite]': step === 2,
              })}
            >
              <h3 className="font-600">Consultation + Demo</h3>
              <p className="text-xs">
                Meet your point-of-contacts and get a rundown of Happitu.
              </p>
            </li>
            <li className="p-4 border-l border-t-4 border-t-secondary-tan">
              <h3 className="font-600">Scoping</h3>
              <p className="text-xs">Define your goals, and software integrations.</p>
            </li>
            <li className="p-4 border-l border-t-4 border-t-secondary-tan">
              <h3 className="font-600">Launch 🚀</h3>
            </li>
          </ol>
        </form>
      </Form>
    </div>
  )
}
